import { AnimatePresence, motion } from 'framer-motion';
import type { FormEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { FiDownload, FiX } from 'react-icons/fi';

import type { Product } from '../data/products';
import OptimizedImage from './OptimizedImage';

interface WorkbookDownloadPopupProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product | null;
}

interface FormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

// Mobile browser detection utilities
const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  );
};

const isIOS = (): boolean => {
  if (typeof window === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
};

const isSafari = (): boolean => {
  if (typeof window === 'undefined') return false;
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
};

const FORM_COMPLETED_KEY = 'konnect_form_completed';

const WorkbookDownloadPopup = ({
  isOpen,
  onClose,
  product,
}: WorkbookDownloadPopupProps) => {
  const downloadConfig = product?.downloadConfig;
  const pdfUrl = downloadConfig?.pdfUrl ?? '';
  const downloadFileName = downloadConfig?.downloadFileName ?? '';
  const eventName = downloadConfig?.eventName ?? '';
  const downloadKey = product ? `konnect_download_${product.slug}` : '';
  const [formData, setFormData] = useState<FormData>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const [hasCompletedForm, setHasCompletedForm] = useState(false);
  const [isMobile] = useState(isMobileDevice());

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setHasDownloaded(localStorage.getItem(downloadKey) === 'true');
      setHasCompletedForm(localStorage.getItem(FORM_COMPLETED_KEY) === 'true');
    }
  }, [downloadKey]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.first_name.trim())
      errors.first_name = 'First name is required';
    if (!formData.last_name.trim()) errors.last_name = 'Last name is required';

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else {
      const phoneRegex = /^\+?[0-9]{10,15}$/;
      if (!phoneRegex.test(formData.phone.replace(/[\s()-]/g, ''))) {
        errors.phone = 'Please enter a valid phone number';
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const triggerDownload = useCallback(async () => {
    try {
      const isIOSDevice = isIOS();
      const isSafariBrowser = isSafari();
      const isMobileDevice = isMobile;

      // iOS Safari and mobile Safari: Open in new tab (download attribute not fully supported)
      if (isIOSDevice || (isMobileDevice && isSafariBrowser)) {
        window.open(pdfUrl, '_blank');
        localStorage.setItem(downloadKey, 'true');
        setHasDownloaded(true);
        return;
      }

      // Mobile browsers (Android Chrome, etc.): Direct link with download attribute
      if (isMobileDevice) {
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = downloadFileName;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        localStorage.setItem(downloadKey, 'true');
        setHasDownloaded(true);
        return;
      }

      // Desktop browsers: Fetch blob and trigger download (existing behavior)
      const response = await fetch(pdfUrl, { mode: 'cors' });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      localStorage.setItem(downloadKey, 'true');
      setHasDownloaded(true);
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback: Open in new tab if blob download fails
      window.open(pdfUrl, '_blank');
    }
  }, [isMobile, pdfUrl, downloadFileName, downloadKey]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    setLoading(true);
    setError(null);

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      const payload = {
        ...formData,
        phone: formData.phone?.trim() || undefined,
      };

      const isLocal =
        typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' ||
          window.location.hostname === '127.0.0.1' ||
          window.location.hostname === '::1');
      const baseUrl = isLocal
        ? 'http://localhost:3000'
        : 'https://konnect-api.azurewebsites.net';

      const createUserResponse = await fetch(
        `${baseUrl}/api/customerio/people`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        },
      );

      const createUserBody: any = await createUserResponse
        .json()
        .catch(() => null);
      const created =
        createUserResponse.status === 201 && createUserBody?.existed === false;

      if (
        !createUserResponse.ok &&
        createUserResponse.status !== 409 &&
        createUserResponse.status !== 200
      ) {
        const message = createUserBody?.message || 'Failed to submit form';
        throw new Error(message);
      }

      if (created) {
        await fetch(
          `${baseUrl}/api/customerio/people/${encodeURIComponent(formData.email)}/events/workbook-download`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ source: 'web', workbook: eventName }),
          },
        );
      }

      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
      });
      setSuccess(true);
      localStorage.setItem(FORM_COMPLETED_KEY, 'true');
      setHasCompletedForm(true);

      // Trigger automatic download
      triggerDownload();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'An unexpected error occurred',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = useCallback(() => {
    onClose();
    setTimeout(() => {
      setSuccess(false);
      setError(null);
      setFieldErrors({});
    }, 300);
  }, [onClose]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose]);

  if (!product?.downloadConfig) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
          onClick={handleBackdropClick}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3, type: 'spring', damping: 25 }}
            className="relative w-full max-w-lg bg-card dark:bg-panel rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
            role="dialog"
            aria-modal="true"
            aria-labelledby="download-popup-title"
          >
            <button
              onClick={handleClose}
              className="absolute top-3 right-3 z-10 p-2.5 rounded-full bg-card/90 dark:bg-dark-card/90 hover:bg-card dark:hover:bg-dark-card shadow-md transition-all duration-200 hover:scale-110"
              aria-label="Close popup"
            >
              <FiX className="w-5 h-5 text-theme-primary" />
            </button>

            <div className="flex flex-col">
              <div className="w-full bg-gradient-to-br from-brand-primary/20 to-brand-secondary/20 p-4 sm:p-6 md:p-8 flex items-center justify-center">
                <div className="w-48 sm:w-56 md:w-64 lg:w-72 bg-card dark:bg-panel rounded-2xl shadow-2xl p-2 overflow-hidden">
                  <div className="rounded-xl overflow-hidden">
                    <OptimizedImage
                      src={product.image}
                      alt={product.title}
                      priority={true}
                    />
                  </div>
                </div>
              </div>

              <div className="w-full px-6 py-6">
                {!success && !hasDownloaded && !hasCompletedForm ? (
                  <>
                    <h2
                      id="download-popup-title"
                      className="text-xl md:text-2xl font-bold text-theme-primary mb-2 text-center"
                    >
                      Download {product.title}
                    </h2>
                    <p className="text-theme-secondary mb-6 text-sm text-center">
                      Enter your details below to receive your free workbook.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label
                            htmlFor="download_first_name"
                            className="block text-sm font-medium text-theme-secondary mb-1"
                          >
                            First Name *
                          </label>
                          <input
                            type="text"
                            id="download_first_name"
                            name="first_name"
                            value={formData.first_name}
                            onChange={handleInputChange}
                            placeholder="First name"
                            className={`w-full px-4 py-3 rounded-lg border ${fieldErrors.first_name ? 'border-red-500' : 'border-theme'} bg-card dark:bg-panel text-theme-primary focus:outline-none focus:ring-2 focus:ring-secondary ring-offset-surface`}
                            required
                          />
                          {fieldErrors.first_name && (
                            <p className="mt-1 text-sm text-red-500">
                              {fieldErrors.first_name}
                            </p>
                          )}
                        </div>

                        <div>
                          <label
                            htmlFor="download_last_name"
                            className="block text-sm font-medium text-theme-secondary mb-1"
                          >
                            Last Name *
                          </label>
                          <input
                            type="text"
                            id="download_last_name"
                            name="last_name"
                            value={formData.last_name}
                            onChange={handleInputChange}
                            placeholder="Last name"
                            className={`w-full px-4 py-3 rounded-lg border ${fieldErrors.last_name ? 'border-red-500' : 'border-theme'} bg-card dark:bg-panel text-theme-primary focus:outline-none focus:ring-2 focus:ring-secondary ring-offset-surface`}
                            required
                          />
                          {fieldErrors.last_name && (
                            <p className="mt-1 text-sm text-red-500">
                              {fieldErrors.last_name}
                            </p>
                          )}
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="download_email"
                          className="block text-sm font-medium text-theme-secondary mb-1"
                        >
                          Email *
                        </label>
                        <input
                          type="email"
                          id="download_email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="your@email.com"
                          className={`w-full px-4 py-3 rounded-lg border ${fieldErrors.email ? 'border-red-500' : 'border-theme'} bg-card dark:bg-panel text-theme-primary focus:outline-none focus:ring-2 focus:ring-secondary ring-offset-surface`}
                          required
                        />
                        {fieldErrors.email && (
                          <p className="mt-1 text-sm text-red-500">
                            {fieldErrors.email}
                          </p>
                        )}
                      </div>

                      <div>
                        <label
                          htmlFor="download_phone"
                          className="block text-sm font-medium text-theme-secondary mb-1"
                        >
                          Phone *
                        </label>
                        <input
                          type="tel"
                          id="download_phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="+1 (555) 123-4567"
                          className={`w-full px-4 py-3 rounded-lg border ${fieldErrors.phone ? 'border-red-500' : 'border-theme'} bg-card dark:bg-panel text-theme-primary focus:outline-none focus:ring-2 focus:ring-secondary ring-offset-surface`}
                          required
                        />
                        {fieldErrors.phone && (
                          <p className="mt-1 text-sm text-red-500">
                            {fieldErrors.phone}
                          </p>
                        )}
                      </div>

                      {error && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="text-red-500 text-sm p-3 bg-red-50 dark:bg-red-900/20 rounded-lg"
                        >
                          {error}
                        </motion.div>
                      )}

                      <motion.button
                        type="submit"
                        disabled={loading}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="w-full btn-primary flex justify-center items-center gap-2 py-3"
                      >
                        {loading ? (
                          <svg
                            className="animate-spin h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                        ) : (
                          <>
                            <FiDownload className="w-5 h-5" />
                            Download Free Workbook
                          </>
                        )}
                      </motion.button>

                      <p className="text-xs text-theme-secondary text-center">
                        By downloading, you agree to our{' '}
                        <a
                          href="/privacy-policy"
                          className="text-secondary hover:underline"
                        >
                          Privacy Policy
                        </a>{' '}
                        and{' '}
                        <a
                          href="/terms-of-service"
                          className="text-secondary hover:underline"
                        >
                          Terms of Service
                        </a>
                      </p>
                    </form>
                  </>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center py-4"
                  >
                    <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FiDownload className="w-8 h-8 text-green-600 dark:text-green-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-theme-primary mb-2">
                      {hasDownloaded && !success
                        ? 'Download Again?'
                        : 'Thank You!'}
                    </h3>
                    <p className="text-theme-secondary mb-6">
                      {hasDownloaded && !success ? (
                        "You've already downloaded this workbook."
                      ) : (
                        <>
                          {isMobile ? (
                            <>
                              {isIOS() || isSafari() ? (
                                <>
                                  Your workbook will open in a new tab.
                                  <br />
                                  Tap the share icon and select "Save to Files"
                                  to download.
                                </>
                              ) : (
                                <>
                                  Your download should begin automatically.
                                  <br />
                                  Check your notifications or downloads folder.
                                </>
                              )}
                            </>
                          ) : (
                            <>
                              Your download should begin automatically.
                              <br />
                              If it doesn't start, click the button below.
                            </>
                          )}
                        </>
                      )}
                    </p>
                    <div className="flex flex-col gap-3">
                      <motion.button
                        onClick={triggerDownload}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="btn-primary flex items-center justify-center gap-2 py-3"
                      >
                        <FiDownload className="w-5 h-5" />
                        {hasDownloaded && !success
                          ? 'Download Again'
                          : 'Download Now'}
                      </motion.button>
                      <button
                        onClick={handleClose}
                        className="text-sm text-theme-secondary hover:text-theme-primary transition-colors"
                      >
                        Close
                      </button>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WorkbookDownloadPopup;
